import 'babel-polyfill';
import React from 'react';
import { render } from 'react-dom';
import { Howl, Howler } from 'howler';
import { createBrowserHistory } from 'history';
import { initialState as jPlayerInitialState } from 'react-jplayer';
import { initialState as jPlaylistInitialState } from 'react-jplaylist';
import { datadogRum } from '@datadog/browser-rum';
import configureStore from './store/configureStore';
import testHooks from './testHooks';
import Root from './Root';
import './stylesheets/main.scss';
import LoadingPage from './common/components/LoadingPage';
import TRODemoVideoContainer from './trc/pages/demoVideos/TRODemoVideoContainer';
import { registerServiceWorker } from './common/utils/offlineUtils';
import LCDemoVideoContainer from './lc/pages/demoVideo/LCDemoVideoContainer';
import { isIOS15Safari, isFromARSource } from './common/utils/browserUtils';
import concealStudentData from './common/utils/string';
import { datadogLogs } from '@datadog/browser-logs';

require.context('./images/owl', true, /\.(png|atlas|json)$/);

/*
    On IOS 15 there is an issue where media files do not load in properly through service
    workers and the browser continuously retries the network request, so we dont register it for now.
*/
// We switch to Content-cache header in S3 bucket setting to provide mp3 files for iOS
registerServiceWorker();

window.ENV = 'production';

if (window.ENV && !window.ENV.startsWith('{creds')) {
    try {
        datadogRum.init({
            applicationId: '87b68cd6-ad0a-47e1-8505-8288115ad1eb',
            clientToken: 'pub45488306e6b2956a7f09b0fc1ae52700',
            site: 'datadoghq.com',
            service: 'lantern-assessment',
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            enablePrivacyForActionName: true,
            env: window.ENV,
            defaultPrivacyLevel: 'mask',
            trackResources: true,
            trackLongTasks: true,
            trackFrustrations: true,
            silentMultipleInit: true,
            trackSessionAcrossSubdomains: true,
            version: '070208f2a03883de44eb702083d41af407556687',
            allowedTracingUrls: [/^https:\/\/.*\.amplify\.com/, /^https:\/\/.*\.aws\.wgen\.net/],
            beforeSend: event => {
                // remove student name and grade from the tracked event
                if (event.action && event.action.target) {
                    event.action.target.name = concealStudentData(event.action.target.name);
                }
            },
        });

        datadogRum.startSessionReplayRecording();

        datadogLogs.init({
            clientToken: 'pub45488306e6b2956a7f09b0fc1ae52700',
            site: 'datadoghq.com',
            service: 'lantern-assessment',
            env: window.ENV,
            sessionSampleRate: 100,
            forwardErrorsToLogs: true,
            trackSessionAcrossSubdomains: true,
            version: '070208f2a03883de44eb702083d41af407556687',
        });
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('DataDog initialization failed:', error);
    }
}

const createEmptySound = () =>
    // This is a workaround for enabling auto play for audio on mobile
    // Start screen does not emit any sound, so howler callbacks for touchend/touchstart
    // setup too late
    new Howl({
        src: ['empty'],
        preload: false,
    });
render(<LoadingPage />, document.getElementById('app'));

const initialState = {
    jPlayers: jPlayerInitialState([TRODemoVideoContainer, LCDemoVideoContainer]),
    jPlaylists: jPlaylistInitialState([TRODemoVideoContainer, LCDemoVideoContainer]),
};

// Configure Howler based on source (AR external URL vs standard assessment)
// AR source (TRO): Use defaults (usingWebAudio: true, autoSuspend: true) to avoid AudioContext conflicts causing silent recordings on iPad
if (!isFromARSource()) {
    // Standard assessments: Apply iOS15 Safari workaround (Web Audio API issues)
    Howler.usingWebAudio = !isIOS15Safari();
    // Prevent 'suspending' state after 30s idle: https://github.com/goldfire/howler.js/issues/1524
    Howler.autoSuspend = !isIOS15Safari(false);
}
// Note: StartView.initAudio() re-enables Web Audio API for TRO accessed without AR source

const history = createBrowserHistory();

const storePromise = configureStore(initialState, history);

storePromise.then(store => {
    window.d394cd2151 = testHooks(store);
    createEmptySound();

    render(<Root store={store} history={history} />, document.getElementById('app'));
});
